import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search, Send, Loader2, Mail, MapPin, MoreHorizontal,
  Inbox as InboxIcon, MessageCircle, Clock, CheckCircle2, Archive, AlertOctagon, ChevronDown,
} from "lucide-react";
import { statusByValue } from "@/lib/constants";

const TABS = [
  { key: "unread",   label: "Unread",   icon: InboxIcon,     tone: "text-brand-dark" },
  { key: "active",   label: "Active",   icon: MessageCircle, tone: "text-blue-600" },
  { key: "waiting",  label: "Waiting",  icon: Clock,         tone: "text-amber-600" },
  { key: "resolved", label: "Resolved", icon: CheckCircle2,  tone: "text-emerald-600" },
  { key: "archived", label: "Archived", icon: Archive,       tone: "text-slate-500" },
  { key: "spam",     label: "Spam",     icon: AlertOctagon,  tone: "text-red-600" },
];

const STATUS_BADGE = {
  unread:   "bg-brand-light text-brand-dark border-[#86EFAC]",
  active:   "bg-sky-50 text-sky-700 border-sky-200",
  waiting:  "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-slate-100 text-slate-600 border-slate-200",
  spam:     "bg-red-50 text-red-700 border-red-200",
};

export default function Inbox() {
  const [tab, setTab] = useState("unread");
  const [counts, setCounts] = useState({ unread: 0, active: 0, waiting: 0, resolved: 0, archived: 0, spam: 0 });
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [contactDetails, setContactDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulk, setBulk] = useState(new Set());
  const [searchParams] = useSearchParams();
  const scrollRef = useRef(null);

  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/conversations/counts"); setCounts(data); } catch {}
  }, []);

  const loadConvs = useCallback(async () => {
    try {
      const params = { status: tab };
      if (search) params.search = search;
      const { data } = await api.get("/conversations", { params });
      setConversations(data);
      if (data.length && (!selected || !data.find((c) => c.id === selected.id))) setSelected(data[0]);
      if (!data.length) { setSelected(null); setData(null); }
    } catch (e) {
      toast.error("Could not load conversations");
    }
  }, [tab, search, selected]);

  useEffect(() => { loadCounts(); loadConvs(); }, [tab, search]);  // eslint-disable-line

  useEffect(() => {
    const target = searchParams.get("conversation");
    if (target) {
      api.get("/conversations").then(({ data }) => {
        const c = data.find((c) => c.id === target);
        if (c) { setTab(c.status || "active"); setSelected(c); }
      }).catch(() => {});
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/conversations/${selected.id}/messages`).then(({ data }) => {
      setData(data);
      setLoading(false);
      // Opening promoted unread→active server-side; refresh counts
      loadCounts();
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    }).catch(() => setLoading(false));
    api.get(`/contacts/${selected.contact_id}`).then(({ data }) => setContactDetails(data)).catch(() => {});
  }, [selected?.id]);  // eslint-disable-line

  const send = async () => {
    if (!text.trim() || !selected) return;
    const body = text;
    setText("");
    try {
      await api.post(`/conversations/${selected.id}/send`, { conversation_id: selected.id, body });
      const { data } = await api.get(`/conversations/${selected.id}/messages`);
      setData(data);
      loadConvs(); loadCounts();
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    } catch (e) { toast.error("Send failed"); setText(body); }
  };

  const setStatus = async (newStatus, conversationId) => {
    const target = conversationId || selected?.id;
    if (!target) return;
    try {
      await api.patch(`/conversations/${target}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      await loadConvs(); await loadCounts();
      if (selected?.id === target) {
        setSelected((prev) => prev ? { ...prev, status: newStatus } : prev);
      }
    } catch (e) { toast.error("Failed to update status"); }
  };

  const bulkSetStatus = async (newStatus) => {
    if (bulk.size === 0) return;
    try {
      const { data } = await api.post("/conversations/bulk-status", {
        conversation_ids: Array.from(bulk), status: newStatus,
      });
      toast.success(`${data.updated} conversation${data.updated !== 1 ? "s" : ""} → ${newStatus}`);
      setBulk(new Set());
      await loadConvs(); await loadCounts();
    } catch { toast.error("Bulk update failed"); }
  };

  const toggleBulk = (id) => { const s = new Set(bulk); s.has(id) ? s.delete(id) : s.add(id); setBulk(s); };

  return (
    <div className="flex h-screen bg-white" data-testid="inbox-page">
      {/* Conversation list */}
      <div className="w-96 shrink-0 border-r border-slate-200 flex flex-col">
        <div className="px-4 pt-4 pb-2 border-b border-slate-200">
          <h1 className="font-heading text-lg font-semibold text-slate-900">Inbox</h1>
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by name, phone, message…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="inbox-search-input" />
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-3 -mb-px overflow-x-auto" data-testid="inbox-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              const n = counts[t.key] || 0;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setBulk(new Set()); }}
                  data-testid={`inbox-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                    active ? "bg-brand-light text-brand-dark" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${active ? "text-brand-dark" : t.tone}`} />
                  {t.label}
                  {n > 0 && (
                    <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-brand text-white" : "bg-slate-200 text-slate-700"}`} data-testid={`inbox-tab-count-${t.key}`}>{n}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bulk action bar */}
        {bulk.size > 0 && (
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2 text-xs" data-testid="inbox-bulk-bar">
            <span className="font-medium text-slate-700">{bulk.size} selected</span>
            <div className="ml-auto flex gap-1">
              {["active","waiting","resolved","archived","spam"].map((s) => (
                <Button key={s} size="sm" variant="outline" onClick={() => bulkSetStatus(s)} data-testid={`inbox-bulk-${s}`} className="h-7 text-xs">{s}</Button>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No {tab} conversations</div>
          ) : conversations.map((c) => (
            <div key={c.id} className={`group flex items-stretch border-b border-slate-100 hover:bg-slate-50 transition-colors ${selected?.id === c.id ? "bg-brand-light" : ""}`}>
              <div className="flex items-center pl-3">
                <Checkbox checked={bulk.has(c.id)} onCheckedChange={() => toggleBulk(c.id)} data-testid={`inbox-bulk-check-${c.id}`} />
              </div>
              <button onClick={() => setSelected(c)} data-testid={`inbox-conversation-${c.id}`} className="flex-1 text-left px-3 py-3">
                <div className="flex items-center gap-3">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-brand text-white text-xs">{c.contact_name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-slate-900 truncate">{c.contact_name}</div>
                      {c.unread_count > 0 && (
                        <span className="text-[10px] bg-brand text-white px-1.5 rounded-full" data-testid={`inbox-unread-${c.id}`}>{c.unread_count}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{c.last_message || "—"}</div>
                    <span className={`mt-1 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[c.status] || STATUS_BADGE.active}`}>{c.status}</span>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 flex flex-col bg-slate-50 min-w-0">
        {selected ? (
          <>
            <div className="p-4 border-b border-slate-200 bg-white flex items-center gap-3">
              <Avatar className="w-9 h-9">
                <AvatarFallback className="bg-brand text-white text-xs">{selected.contact_name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{selected.contact_name}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{selected.contact_phone}</div>
              </div>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[selected.status] || STATUS_BADGE.active}`} data-testid="inbox-current-status">{selected.status}</span>
              {/* Quick actions */}
              <div className="hidden md:flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setStatus("active")} data-testid="inbox-action-active" className="h-8">Active</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus("waiting")} data-testid="inbox-action-waiting" className="h-8">Waiting</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus("resolved")} data-testid="inbox-action-resolved" className="h-8">Resolve</Button>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="inbox-more-menu" className="h-8"><MoreHorizontal className="w-4 h-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setStatus("archived")} data-testid="inbox-action-archive">
                    <Archive className="w-4 h-4 mr-2" />Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatus("spam")} data-testid="inbox-action-spam" className="text-red-600">
                    <AlertOctagon className="w-4 h-4 mr-2" />Mark as spam
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-2">
              {loading ? (
                <div className="text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
              ) : (data?.messages || []).map((m) => (
                <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-md px-3 py-2 rounded-2xl text-sm ${
                    m.direction === "outbound"
                      ? m.status === "failed"
                        ? "bg-red-50 border border-red-200 text-red-900 rounded-br-sm"
                        : "bg-brand text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    {m.status === "failed" && m.error && (
                      <div className="mt-1.5 pt-1.5 border-t border-red-200 text-[11px] text-red-700 font-mono break-all">⚠ {m.error}</div>
                    )}
                    <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? (m.status === "failed" ? "text-red-500" : "text-emerald-100") : "text-slate-400"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {m.direction === "outbound" && ` · ${m.status}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()} placeholder="Type a message…" data-testid="inbox-message-input" />
                <Button onClick={send} className="bg-brand hover:bg-brand-dark text-white" data-testid="inbox-send-button"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-sm text-slate-400">Select a conversation</div>
        )}
      </div>

      {/* Right panel */}
      {selected && (
        <div className="w-72 shrink-0 border-l border-slate-200 bg-white p-5 overflow-y-auto">
          <div className="text-center">
            <Avatar className="w-16 h-16 mx-auto"><AvatarFallback className="bg-brand text-white">{selected.contact_name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
            <div className="mt-3 font-heading font-semibold text-slate-900">{selected.contact_name}</div>
            <div className="text-xs text-slate-500 font-mono">{selected.contact_phone}</div>
          </div>
          {contactDetails && (
            <>
              <div className="mt-5"><div className="label-caps mb-2">Lead status</div>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusByValue(contactDetails.contact.status).color}`}>{statusByValue(contactDetails.contact.status).label}</span>
              </div>
              <div className="mt-5 space-y-2 text-xs text-slate-600">
                {contactDetails.contact.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" />{contactDetails.contact.email}</div>}
                {contactDetails.contact.city && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" />{contactDetails.contact.city}</div>}
              </div>
              {contactDetails.tags.length > 0 && (
                <div className="mt-5"><div className="label-caps mb-2">Tags</div>
                  <div className="flex flex-wrap gap-1.5">
                    {contactDetails.tags.map((t) => (<span key={t.id} className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: t.color }}>{t.name}</span>))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
